import * as XLSX from 'xlsx';
import { getAllArticles } from '../models/articleAPI';

// Column definitions for each detail-table template.
// Only CODE_ART is mandatory when the user fills the file;
// other columns fall back to article defaults on import.
export const TEMPLATE_CONFIG = {
  correctionstock: {
    fileName: 'modele_correction_stock.xlsx',
    sheetName: 'CorrectionStock',
    columns: ['CODE_ART', 'QTE', 'REMARQUE'],
  },
  demarque: {
    fileName: 'modele_demarque.xlsx',
    sheetName: 'Demarque',
    columns: ['CODE_ART', 'QTE', 'REMARQUE'],
  },
  reception: {
    fileName: 'modele_reception.xlsx',
    sheetName: 'Reception',
    columns: ['CODE_ART', 'QTE', 'PA_HT', 'PA_TTC', 'TVA', 'REMISE'],
  },
  inventaire: {
    fileName: 'modele_inventaire.xlsx',
    sheetName: 'Inventaire',
    columns: ['CODE_ART', 'QTE_INV', 'PA_HT', 'PA_TTC'],
  },
};

export const downloadDetailTemplate = (type) => {
  const config = TEMPLATE_CONFIG[type];
  if (!config) return;
  const worksheet = XLSX.utils.aoa_to_sheet([config.columns]);
  worksheet['!cols'] = config.columns.map((col) => ({ wch: Math.max(col.length + 4, 12) }));
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, config.sheetName);
  XLSX.writeFile(workbook, config.fileName);
};

// Handles values typed with a comma decimal separator ("12,5")
const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  const parsed = parseFloat(String(value).trim().replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
};

// Parses an uploaded Excel file and returns raw rows with normalized
// (uppercased/trimmed) header keys. Rows without CODE_ART are skipped.
export const parseDetailExcelFile = async (file) => {
  const buffer = await file.arrayBuffer();
  const workbook = XLSX.read(buffer, { type: 'array' });
  const sheetName = workbook.SheetNames[0];
  if (!sheetName) return [];
  const rawRows = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: '' });

  return rawRows
    .map((rawRow) => {
      const row = {};
      Object.entries(rawRow).forEach(([key, value]) => {
        row[String(key).trim().toUpperCase()] = value;
      });
      return row;
    })
    .map((row) => ({
      ...row,
      CODE_ART: String(row.CODE_ART ?? '').trim(),
    }))
    .filter((row) => row.CODE_ART !== '');
};

// Looks up articles by exact code (case-insensitive) via the articles API.
// Returns a Map of upper-cased code -> article (or undefined when not found).
export const resolveArticlesByCode = async (codes) => {
  const uniqueCodes = [...new Set(codes.map((code) => String(code).trim()).filter(Boolean))];
  const result = new Map();
  const CHUNK_SIZE = 10;

  for (let i = 0; i < uniqueCodes.length; i += CHUNK_SIZE) {
    const chunk = uniqueCodes.slice(i, i + CHUNK_SIZE);
    await Promise.all(
      chunk.map(async (code) => {
        try {
          const data = await getAllArticles({ codeArtSearch: code, page: 1, pageSize: 10 });
          const articles = data?.articles || [];
          const exact = articles.find(
            (article) => String(article.code_art).trim().toUpperCase() === code.toUpperCase()
          );
          if (exact) result.set(code.toUpperCase(), exact);
        } catch (e) {
          // Lookup failure is treated the same as "article not found";
          // the row will be reported as unknown to the user.
        }
      })
    );
  }

  return result;
};

// Turns parsed Excel rows into detail-row objects ready for each screen,
// using article data as fallback for missing prices. Returns
// { items, unknownCodes } where unknownCodes lists CODE_ARTs not found.
export const buildDetailItemsFromExcel = async (rows, type) => {
  const articleMap = await resolveArticlesByCode(rows.map((row) => row.CODE_ART));
  const items = [];
  const unknownCodes = [];

  rows.forEach((row) => {
    const article = articleMap.get(row.CODE_ART.toUpperCase());
    if (!article) {
      unknownCodes.push(row.CODE_ART);
      return;
    }

    const base = {
      CODE_ART: article.code_art,
      ARTICLE: article.ARTICLE || '',
    };

    if (type === 'correctionstock' || type === 'demarque') {
      items.push({
        ...base,
        QTE: Math.abs(toNumber(row.QTE) ?? 1) || 1,
        REMARQUE: String(row.REMARQUE ?? '').trim(),
      });
      return;
    }

    if (type === 'reception') {
      const qte = toNumber(row.QTE) ?? 1;
      const paHT = toNumber(row.PA_HT) ?? Number(article.PA_HT) ?? 0;
      const tva = toNumber(row.TVA) ?? (Number(article.TVA) || 20);
      const paTTC = toNumber(row.PA_TTC) ?? +(paHT * (1 + tva / 100)).toFixed(2);
      items.push({
        ...base,
        QTE: qte,
        PA_HT: paHT,
        PA_TTC: paTTC,
        TVA: tva,
        REMISE: toNumber(row.REMISE) ?? 0,
        GRATUIT: toNumber(row.GRATUIT) ?? 0,
        TotalHT: +(qte * paHT).toFixed(2),
        TotalTTC: +(qte * paTTC).toFixed(2),
      });
      return;
    }

    if (type === 'inventaire') {
      items.push({
        ...base,
        QTE_INV: toNumber(row.QTE_INV) ?? toNumber(row.QTE) ?? 0,
        PA_HT: toNumber(row.PA_HT) ?? Number(article.PA_HT) ?? 0,
        PA_TTC: toNumber(row.PA_TTC) ?? Number(article.PA_TTC) ?? 0,
      });
    }
  });

  return { items, unknownCodes };
};
